/**
 * The kiosk device's own session: an event-bound token from POST /auth/login/shared { event_id, password }.
 * Kept in sessionStorage (it ends when the tab closes) and apart from the normal sign-in, so a kiosk never looks like a
 * signed-in planner to the rest of the app. Every storage call is guarded: private windows and blocked storage must not crash it.
 */
import { getApiKey, getApiUrl } from './PlanningApiClient';
import { ApiError, buildApiError } from '../utils/apiErrors';
import type { DoorCredentials } from './DoorApiClient';

const KEY = 'ans_kiosk_session';

export interface KioskSession {
  token: string;
  projectId: string;
  eventId: string;
}

export function readKioskSession(eventId?: string): KioskSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<KioskSession>;
    if (!s.token || !s.projectId || !s.eventId) return null;
    if (eventId && s.eventId !== eventId) return null;
    return { token: s.token, projectId: s.projectId, eventId: s.eventId };
  } catch {
    return null;
  }
}

export function saveKioskSession(session: KioskSession): void {
  try { sessionStorage.setItem(KEY, JSON.stringify(session)); } catch { /* storage unavailable: the session lasts until reload */ }
}

export function clearKioskSession(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}

export const kioskCredentials = (): DoorCredentials | null => {
  const s = readKioskSession();
  return s ? { token: s.token, projectId: s.projectId } : null;
};

/** Signs a device in to one event. Throws an ApiError whose message is safe to show. */
export async function kioskLogin(eventId: string, password: string): Promise<KioskSession> {
  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}/auth/login/shared`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': getApiKey() },
      body: JSON.stringify({ event_id: eventId, password }),
    });
  } catch {
    throw ApiError.offline();
  }
  const body = await res.json().catch(() => null);
  if (res.status === 401) throw new ApiError('That password is not right for this event. Check it and try again.', { status: 401 });
  if (res.status === 429) throw new ApiError('Too many tries. Wait a minute and try again.', { status: 429 });
  if (!res.ok) throw buildApiError(res.status, body);
  const projectId = body?.project_id as string | undefined;
  const token = body?.token as string | undefined;
  if (!projectId || !token) throw new ApiError('The server did not return a kiosk session. Try again.', { status: res.status });
  const session = { token, projectId, eventId };
  saveKioskSession(session);
  return session;
}
